/**
 * Helpers for recognising Upright Platform deep-link URLs that the model
 * embeds as plain markdown links in chat answers.
 *
 * Background: PR #7045 added a top-level `url` field to several MCP tool
 * results (DMA, Net Impact, Financial Effects, single-matter) and a
 * system-prompt nudge telling the model to embed those as markdown links —
 * e.g. "Upright's DMA for [Storebrand](https://uprightplatform.com/company/.../dma)".
 * Those flow through `react-markdown` as ordinary `<a>` nodes; they are
 * NOT Anthropic citations and never enter the citation registry that backs
 * the numbered `<cite id="<id>"/>` rail. Without help, the user sees a plain
 * inline link with nothing in the margin sidenote rail — defeating the
 * point of the deep-link nudge.
 *
 * This module's job is the recognition step: given an `<a href>` value,
 * answer "is this an Upright platform deep-link?". Callers (the chat's
 * markdown `a`-component override) use the verdict to render a margin
 * sidenote next to the inline link, using the same lilac styling as
 * registered Upright citations so the rail reads as one consistent column.
 *
 * Why a separate module rather than the existing `UPRIGHT_HOST_PATTERN`
 * in `upright-chat-tool-uis.tsx`:
 *
 *   - The host pattern there only covers `uprightplatform.com` /
 *     `uprightproject.com`. Dev environments serve the same code under
 *     `*.uprightplatform.io` (playground/mango/etc.); links the model emits
 *     in those envs use whatever `SERVER_URL` resolves to. Same-origin
 *     detection covers all of them without enumerating the matrix.
 *   - Recognising platform deep-links is independent of the citation
 *     registry. Keeping the helper out of the registry-aware module keeps
 *     the import graph tidy and lets us unit-test it without pulling in
 *     react-markdown.
 *
 * The companion CSS (`.chat-citation-sidenote--platform-link`) and the
 * `<a>`-override branch live in `upright-chat-theme.css` and
 * `upright-chat.tsx` respectively.
 */

/**
 * Hosts whose URLs are treated as Upright Platform deep-links even when the
 * page is being served from a different origin (e.g. Storybook, tests, a
 * preview env that runs the chat against prod URLs).
 *
 * The leading `(^|\.)` anchor lets `uprightplatform.com` and any subdomain
 * (e.g. `playground.uprightplatform.com`) match without false positives on
 * lookalike hosts like `evil-uprightplatform.com.example`.
 */
const UPRIGHT_PLATFORM_HOST_PATTERN =
  /(^|\.)uprightplatform\.com$|(^|\.)uprightproject\.com$|(^|\.)uprightplatform\.io$/i;

/**
 * Try to read the current page's host. Falls back to `null` in non-browser
 * environments (SSR/tests) so callers degrade gracefully — they still match
 * via `UPRIGHT_PLATFORM_HOST_PATTERN`, just without the same-origin path.
 */
function getCurrentHost(): string | null {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') return null;
  return window.location.host;
}

/**
 * Decide whether `href` points at an Upright Platform deep-link.
 *
 * Returns `true` for absolute URLs whose host either:
 *   - matches `UPRIGHT_PLATFORM_HOST_PATTERN` (covers prod + dev domains),
 *     or
 *   - matches the current page's host (covers any preview/staging env not
 *     on a `*.uprightplatform.{com,io}` domain — without this, deep-links
 *     in those envs would silently render as plain anchors).
 *
 * Returns `false` for non-string inputs, malformed URLs, mailto/tel/cite
 * scheme strings, and same-page anchors (`#foo`) — none of those are
 * platform deep-links and we don't want to wrap them in a sidenote.
 */
export function isUprightPlatformLink(href: string | undefined | null): boolean {
  if (typeof href !== 'string' || href.length === 0) return false;
  // Bail on schemes the URL ctor would happily parse but that aren't web
  // navigations — `mailto:` / `tel:` / `data:` URIs aren't deep-links, and
  // the chat already has a dedicated pipeline for `cite:`.
  if (/^(mailto|tel|data|cite):/i.test(href)) return false;
  let parsed: URL;
  try {
    // The chat only ever renders absolute URLs in this branch (the model is
    // instructed to embed full URLs from tool results), so an explicit
    // base is unnecessary — but `new URL()` throws on relative inputs, so
    // we'd lose to that path anyway.
    parsed = new URL(href);
  } catch {
    return false;
  }
  if (UPRIGHT_PLATFORM_HOST_PATTERN.test(parsed.host)) return true;
  const currentHost = getCurrentHost();
  if (currentHost !== null && parsed.host === currentHost) return true;
  return false;
}

/**
 * The in-app path (`/company/123/Acme/dma?x=1#y`) of a platform link to a page of this app, or
 * `null` for any other host, an API URL, a malformed URL or a non-browser context.
 *
 * Only the current host qualifies, which is the host the chat's own links are built on
 * (`SERVER_URL`). Another environment's host is a different app, and `docs.uprightplatform.com`
 * and the marketing site are other sites. API URLs are excluded: the router has no route for them.
 */
export function sameOriginPlatformPath(href: string): string | null {
  const currentHost = getCurrentHost();
  if (currentHost === null) return null;
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }
  if (!/^https?:$/.test(parsed.protocol) || parsed.host !== currentHost) return null;
  if (parsed.pathname === '/api' || parsed.pathname.startsWith('/api/')) return null;
  return parsed.pathname + parsed.search + parsed.hash;
}

/**
 * Read a human-friendly host label from a URL ("uprightplatform.com",
 * "playground.development.uprightplatform.io"). Strips a leading `www.`
 * for cosmetic reasons, mirroring `tryParseHost` in the citation pill.
 *
 * Returns `null` when the URL is malformed so callers can fall back to a
 * generic label rather than rendering a broken sidenote.
 */
export function tryParsePlatformLinkHost(href: string): string | null {
  try {
    return new URL(href).host.replace(/^www\./, '');
  } catch {
    return null;
  }
}

/**
 * Try to extract the company id from an Upright Platform `/company/<id>/...` URL.
 * Used by the citation rail to look up `companyId → visibility` from the
 * tool-result registry (PLT-4548) so the right-margin sidenote next to a
 * platform deep-link can carry a lock badge when the result is org-private.
 *
 * Returns `null` for non-`/company/` paths, malformed URLs, or paths whose
 * second segment is empty — the registry has no entry to look up in any of
 * those cases. The companion calls in the registry already treat "missing"
 * as "render no badge", so failing to extract is safe.
 *
 * Note: group / portfolio deep-links use `/group/...` / `/portfolio/...` —
 * those don't carry the per-variant visibility signal today (the chat MCP
 * does not expose the field on group-scoped tools yet) so we don't try to
 * parse them here.
 */
export function tryParsePlatformCompanyId(href: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }
  // Path looks like "/company/<id>/<...>". `pathname.split('/')` returns
  // ['', 'company', '<id>', ...] for that shape; bail on anything else.
  const segments = parsed.pathname.split('/');
  if (segments.length < 3 || segments[1] !== 'company') return null;
  const id = segments[2];
  return id && id.length > 0 ? id : null;
}
